import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import {
  clusterColor,
  computeGraphViewBox,
  edgePath,
  hostAccent,
  layoutRelationshipGraph,
  truncateLabel,
  type LayoutEntity,
  type LayoutGroup,
} from '@/utils/relationshipGraphLayout';

export interface MeshGraphNode {
  id: string;
  label: string;
  url?: string;
  favicon?: string;
  topics?: string[];
  groupId?: string;
}

export interface MeshGraphEdge {
  id: string;
  source: string;
  target: string;
  strength: number;
  type: 'semantic' | 'temporal' | 'referential' | 'contextual';
  label?: string;
}

const EDGE_COLORS: Record<MeshGraphEdge['type'], string> = {
  semantic: '#8B5CF6',
  temporal: '#10B981',
  referential: '#3B82F6',
  contextual: '#F59E0B',
};

const NODE_W = 196;
const NODE_H = 52;

function hostFromUrl(url?: string): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function toLayoutEntities(nodes: MeshGraphNode[]): LayoutEntity[] {
  return nodes.map((node) => {
    const host = hostFromUrl(node.url);
    const cluster =
      node.topics?.[0] ??
      node.groupId?.replace(/^cluster-/, '') ??
      (host ? `site:${host}` : 'general');
    const color = cluster.startsWith('site:')
      ? hostAccent(host)
      : clusterColor(cluster);

    return {
      id: node.id,
      name: node.label,
      type: 'tab',
      color,
      x: 0,
      y: 0,
      size: 20,
      width: NODE_W,
      height: NODE_H,
      cluster,
      host,
      data: {
        url: node.url,
        favicon:
          node.favicon ||
          (host ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32` : undefined),
        topics: node.topics,
      },
    };
  });
}

function toLayoutGroups(nodes: MeshGraphNode[]): LayoutGroup[] {
  const byGroup = new Map<string, string[]>();
  for (const node of nodes) {
    const key = node.groupId || node.topics?.[0] || 'general';
    const list = byGroup.get(key) ?? [];
    list.push(node.id);
    byGroup.set(key, list);
  }
  return [...byGroup.entries()].map(([id, entities]) => ({
    id,
    name: id.replace(/^cluster-/, ''),
    color: clusterColor(id.replace(/^cluster-/, '')),
    entities,
  }));
}

function computeForceLayout(
  entities: LayoutEntity[],
  edges: MeshGraphEdge[],
  width: number,
  height: number,
): LayoutEntity[] {
  if (entities.length === 0) return [];

  const simNodes = entities.map((e) => ({ ...e }));
  const links = edges.map((e) => ({ source: e.source, target: e.target }));

  const simulation = d3
    .forceSimulation(simNodes as d3.SimulationNodeDatum & LayoutEntity[])
    .force(
      'link',
      d3
        .forceLink(links)
        .id((d) => (d as LayoutEntity).id)
        .distance(160)
        .strength(0.35),
    )
    .force('charge', d3.forceManyBody().strength(-520))
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force('collision', d3.forceCollide().radius(58))
    .force('x', d3.forceX(width / 2).strength(0.04))
    .force('y', d3.forceY(height / 2).strength(0.04))
    .stop();

  for (let i = 0; i < 320; i += 1) simulation.tick();

  return simNodes.map((n) => ({
    ...(n as LayoutEntity),
    width: NODE_W,
    height: NODE_H,
  }));
}

function computeRadialLayout(
  entities: LayoutEntity[],
  width: number,
  height: number,
): LayoutEntity[] {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.max(120, Math.min(width, height) / 2 - 90);

  return entities.map((entity, index) => {
    const angle = (2 * Math.PI * index) / Math.max(entities.length, 1) - Math.PI / 2;
    return {
      ...entity,
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
      width: NODE_W,
      height: NODE_H,
    };
  });
}

interface MeshGraphCanvasProps {
  nodes: MeshGraphNode[];
  edges: MeshGraphEdge[];
  layout: 'force' | 'hierarchical' | 'radial';
  filterType?: string | null;
  selectedNodeId?: string | null;
  selectedEdgeId?: string | null;
  searchQuery?: string;
  onNodeClick?: (nodeId: string) => void;
  onEdgeClick?: (edgeId: string) => void;
  className?: string;
}

export function MeshGraphCanvas({
  nodes,
  edges,
  layout,
  filterType,
  selectedNodeId,
  selectedEdgeId,
  searchQuery = '',
  onNodeClick,
  onEdgeClick,
  className = '',
}: MeshGraphCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPan, setLastPan] = useState({ x: 0, y: 0 });
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);

  const filteredEdges = useMemo(
    () => edges.filter((e) => !filterType || e.type === filterType),
    [edges, filterType],
  );

  const { entities, graphWidth, graphHeight } = useMemo(() => {
    const base = toLayoutEntities(nodes);
    const groups = toLayoutGroups(nodes);

    if (layout === 'hierarchical') {
      const laidOut = layoutRelationshipGraph(base, groups);
      return { entities: laidOut.entities, graphWidth: laidOut.width, graphHeight: laidOut.height };
    }

    const canvasW = layout === 'radial' ? 900 : 960;
    const canvasH = layout === 'radial' ? 560 : 600;

    if (layout === 'radial') {
      return {
        entities: computeRadialLayout(base, canvasW, canvasH),
        graphWidth: canvasW,
        graphHeight: canvasH,
      };
    }

    return {
      entities: computeForceLayout(base, filteredEdges, canvasW, canvasH),
      graphWidth: canvasW,
      graphHeight: canvasH,
    };
  }, [nodes, layout, filteredEdges]);

  const filteredEntities = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return entities;
    return entities.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.host.toLowerCase().includes(q) ||
        e.cluster.toLowerCase().includes(q),
    );
  }, [entities, searchQuery]);

  const visibleIds = useMemo(
    () => new Set(filteredEntities.map((e) => e.id)),
    [filteredEntities],
  );

  const highlightedIds = useMemo(() => {
    const focusId = selectedNodeId ?? hoveredNodeId;
    if (!focusId) return null;
    const ids = new Set<string>([focusId]);
    for (const edge of filteredEdges) {
      if (edge.source === focusId) ids.add(edge.target);
      if (edge.target === focusId) ids.add(edge.source);
    }
    return ids;
  }, [selectedNodeId, hoveredNodeId, filteredEdges]);

  const columnHeaders = useMemo(() => {
    if (layout !== 'hierarchical') return [];
    const seen = new Set<string>();
    const headers: { x: number; label: string; color: string; count: number }[] = [];
    for (const e of entities) {
      const label = String(e.data?._colHeader ?? e.cluster);
      const colX = e.data?._colX as number | undefined;
      if (colX == null || seen.has(label)) continue;
      seen.add(label);
      headers.push({
        x: colX + (e.width ?? NODE_W) / 2,
        label,
        color: String(e.data?._colAccent ?? e.color),
        count: Number(e.data?._colCount ?? 1),
      });
    }
    return headers;
  }, [entities, layout]);

  const graphViewBox = computeGraphViewBox(entities, graphWidth, graphHeight);

  const resetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    resetView();
  }, [layout, nodes.length, resetView]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      setIsPanning(true);
      setLastPan({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setPan({ x: e.clientX - lastPan.x, y: e.clientY - lastPan.y });
    }
  };

  const handleMouseUp = () => setIsPanning(false);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.92 : 1.08;
    setZoom((z) => Math.min(2.5, Math.max(0.35, z * delta)));
  };

  const entityById = useMemo(() => new Map(entities.map((e) => [e.id, e])), [entities]);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden bg-[radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.07)_1px,transparent_0)] bg-[length:22px_22px] ${className}`}
    >
      {/* Zoom controls */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-1 rounded-lg border border-khiimori-border/20 bg-khiimori-bg-elevated/90 backdrop-blur p-1 shadow-lg">
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(z / 1.15, 0.35))}
          className="p-1.5 rounded-md hover:bg-khiimori-bg-tertiary text-khiimori-text-secondary"
          aria-label="Zoom out"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="text-xs text-khiimori-text-muted min-w-[40px] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(z * 1.15, 2.5))}
          className="p-1.5 rounded-md hover:bg-khiimori-bg-tertiary text-khiimori-text-secondary"
          aria-label="Zoom in"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={resetView}
          className="p-1.5 rounded-md hover:bg-khiimori-bg-tertiary text-khiimori-text-secondary"
          aria-label="Reset view"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      <div
        className="absolute inset-0 cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        <svg className="w-full h-full" viewBox={graphViewBox} preserveAspectRatio="xMidYMid meet">
          <defs>
            <filter id="mesh-node-glow" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            {filteredEntities.map((entity) => (
              <clipPath key={`clip-${entity.id}`} id={`mesh-clip-${entity.id}`}>
                <rect x={46} y={10} width={(entity.width ?? NODE_W) - 54} height={(entity.height ?? NODE_H) - 14} rx={4} />
              </clipPath>
            ))}
          </defs>

          <g
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: 'center',
            }}
          >
            {layout === 'hierarchical' &&
              columnHeaders.map((col) => {
                const colEntities = entities.filter(
                  (e) => String(e.data?._colHeader ?? e.cluster) === col.label,
                );
                if (colEntities.length === 0) return null;
                const colX = Number(colEntities[0]?.data?._colX ?? 0);
                const colW = colEntities[0]?.width ?? NODE_W;
                const minY = Math.min(...colEntities.map((e) => e.y)) - 32;
                const maxY = Math.max(...colEntities.map((e) => e.y)) + 32;
                return (
                  <g key={col.label}>
                    <rect
                      x={colX - 10}
                      y={minY}
                      width={colW + 20}
                      height={maxY - minY}
                      rx={14}
                      fill={col.color}
                      fillOpacity={0.07}
                      stroke={col.color}
                      strokeOpacity={0.22}
                    />
                    <text x={col.x} y={34} textAnchor="middle" fontSize={12} fontWeight={600} fill={col.color}>
                      {col.label}
                    </text>
                    <text x={col.x} y={48} textAnchor="middle" fontSize={10} fill="#94a3b8">
                      {col.count} tab{col.count === 1 ? '' : 's'}
                    </text>
                  </g>
                );
              })}

            {filteredEdges.map((edge) => {
              const source = entityById.get(edge.source);
              const target = entityById.get(edge.target);
              if (!source || !target) return null;
              if (!visibleIds.has(source.id) || !visibleIds.has(target.id)) return null;

              const isActive =
                selectedEdgeId === edge.id ||
                hoveredEdgeId === edge.id ||
                (highlightedIds?.has(source.id) && highlightedIds.has(target.id));
              const dimmed = highlightedIds && !isActive;

              return (
                <path
                  key={edge.id}
                  d={edgePath(source.x, source.y, target.x, target.y)}
                  fill="none"
                  stroke={EDGE_COLORS[edge.type]}
                  strokeWidth={isActive ? 2.5 : 1.5}
                  strokeOpacity={dimmed ? 0.1 : isActive ? 0.95 : 0.42}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdgeClick?.(edge.id);
                  }}
                  onMouseEnter={() => setHoveredEdgeId(edge.id)}
                  onMouseLeave={() => setHoveredEdgeId(null)}
                />
              );
            })}

            {filteredEntities.map((entity) => {
              const w = entity.width ?? NODE_W;
              const h = entity.height ?? NODE_H;
              const isSelected = selectedNodeId === entity.id;
              const isHovered = hoveredNodeId === entity.id;
              const isHighlighted = highlightedIds?.has(entity.id);
              const dimmed = highlightedIds && !isHighlighted && !isSelected;
              const favicon = String(entity.data?.favicon ?? '');

              return (
                <g
                  key={entity.id}
                  transform={`translate(${entity.x - w / 2}, ${entity.y - h / 2})`}
                  className="cursor-pointer"
                  opacity={dimmed ? 0.28 : 1}
                  filter={isSelected || isHovered ? 'url(#mesh-node-glow)' : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNodeClick?.(entity.id);
                  }}
                  onMouseEnter={() => setHoveredNodeId(entity.id)}
                  onMouseLeave={() => setHoveredNodeId(null)}
                >
                  <rect
                    width={w}
                    height={h}
                    rx={12}
                    fill="#1e1b2e"
                    stroke={isSelected ? '#a78bfa' : entity.color}
                    strokeWidth={isSelected ? 2.5 : isHovered ? 2 : 1.5}
                    strokeOpacity={isSelected ? 1 : 0.75}
                  />
                  <rect x={0} y={0} width={4} height={h} rx={2} fill={entity.color} />
                  {favicon ? (
                    <image href={favicon} x={12} y={13} width={26} height={26} />
                  ) : (
                    <>
                      <circle cx={25} cy={26} r={13} fill={entity.color} fillOpacity={0.2} />
                      <text x={25} y={26} textAnchor="middle" dominantBaseline="central" fontSize={11} fill={entity.color}>
                        {entity.host ? entity.host.charAt(0).toUpperCase() : '•'}
                      </text>
                    </>
                  )}
                  <text
                    x={46}
                    y={21}
                    fontSize={12}
                    fontWeight={600}
                    fill="#f1f5f9"
                    clipPath={`url(#mesh-clip-${entity.id})`}
                  >
                    {truncateLabel(entity.name, 26)}
                  </text>
                  <text
                    x={46}
                    y={37}
                    fontSize={10}
                    fill="#94a3b8"
                    clipPath={`url(#mesh-clip-${entity.id})`}
                  >
                    {truncateLabel(entity.host || entity.cluster, 30)}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      <div className="absolute bottom-3 left-3 rounded-lg border border-khiimori-border/20 bg-khiimori-bg-elevated/90 backdrop-blur px-3 py-1.5 text-xs text-khiimori-text-muted leading-normal">
        Drag to pan · scroll to zoom
      </div>
    </div>
  );
}
